import { Component, ElementRef, Input, OnChanges, OnInit, ViewChild } from '@angular/core';
import { TranslationService } from '@sdbn/common';
import * as d3 from 'd3';

@Component({
    selector: 'app-bar-lines-chart',
    templateUrl: './bar-lines-chart.component.html',
    styleUrls: ['./bar-lines-chart.component.scss'],
    standalone: false
})
export class BarLinesChartComponent implements OnInit, OnChanges {

  @ViewChild('chart', { static: true }) chartContainer: ElementRef;
  @Input() timeRange: Array<number>;
  @Input() bar: any;
  @Input() lines: Array<any>;

  private margin: any = { top: 20, bottom: 40, left: 50, right: 50 };
  private barChart: any;
  private lineCharts: any;
  private width: number;
  private height: number;
  private xScale: any;
  private yLeftScale: any;
  private yRightScale: any;
  private axisBottom: any;
  private axisLeft: any;
  private axisRight: any;
  private xAxis: any;
  private yAxisLeft: any;
  private yAxisRight: any;

  constructor(private translationService: TranslationService) { }

  ngOnInit() {
    this.createChart();
    this.updateChart();
  }

  ngOnChanges() {
    if (this.barChart || this.lineCharts) {
      this.updateChart();
    }
  }

  createChart() {
    const element = this.chartContainer.nativeElement;
    // Calculate width and heigth based on surrounding container
    const clientWidth = 1000;
    const clientHeight = 400;

    this.width = clientWidth - this.margin.left - this.margin.right;
    this.height = clientHeight - this.margin.top - this.margin.bottom;

    const svg = d3.select(element).append('svg')
      .attr('width', clientWidth + 'px')
      .attr('height', (clientHeight + 40) + 'px');

    // chart plot area
    this.barChart = svg.append('g')
      .attr('class', 'bars')
      .attr('transform', `translate(${this.margin.left}, ${this.margin.top})`);

    this.lineCharts = svg.append('g')
      .attr('class', 'linecharts')
      .attr('transform', `translate(${this.margin.left}, ${this.margin.top})`);


    // define X & Y domains
    const xDomain = this.timeRange.map((d, idx) => d + '');
    // Calculate max values
    const yLeftDomain = this.calculateYLeftDomain();
    const yRightDomain = this.calculateYRightDomain();

    // create scales
    this.xScale = d3.scaleBand().domain(xDomain).range([0, this.width]);
    this.yLeftScale = d3.scaleLinear().domain(yLeftDomain).range([this.height, 0]);
    this.yRightScale = d3.scaleLinear().domain(yRightDomain).range([this.height, 0]);

    this.axisBottom = d3.axisBottom(this.xScale).tickSize(5);
    this.axisLeft = d3.axisLeft(this.yLeftScale).tickSize(5);
    this.axisRight = d3.axisRight(this.yRightScale).tickSize(5);

    // x & y axis
    this.xAxis = svg.append('g')
      .attr('class', 'axis axis-x')
      .attr('transform', `translate(${this.margin.left}, ${this.margin.top + this.height})`)
      .call(this.axisBottom);

    this.yAxisLeft = svg.append('g')
      .attr('class', 'axis axis-y-left')
      .attr('transform', `translate(${this.margin.left}, ${this.margin.top})`)
      .call(this.axisLeft);

    this.yAxisRight = svg.append('g')
      .attr('class', 'axis axis-y-right')
      .attr('transform', `translate(${this.margin.left + this.width}, ${this.margin.top})`)
      .call(this.axisRight);

    svg.append("circle").attr("cx", 60).attr("cy", 415).attr("r", 6).style("fill", "#a7cf99");
    svg.append("circle").attr("cx", 240).attr("cy", 415).attr("r", 6).style("fill", "#ffd738");
    svg.append("circle").attr("cx", 420).attr("cy", 415).attr("r", 6).style("fill", "#85929e");

    let i18n = this.translationService.translate('tmc-yard.ui.reporting.dashboard.bar-line-chart.transport-count');
    let label = i18n == null ? 'Anzahl Anfahrten' : i18n;
    svg.append("text").attr("x", 80).attr("y", 415).text(label).style("font-size", "15px").attr("alignment-baseline", "middle");

    i18n = this.translationService.translate('tmc-yard.ui.reporting.dashboard.bar-line-chart.processing-time');
    label = i18n == null ? 'Durchlaufzeit %' : i18n;
    svg.append("text").attr("x", 260).attr("y", 415).text(label).style("font-size", "15px").attr("alignment-baseline", "middle");

    i18n = this.translationService.translate('tmc-yard.ui.reporting.dashboard.bar-line-chart.processing-time-quote');
    label = i18n == null ? 'Durchlaufzeit eingehalten' : i18n;
    svg.append("text").attr("x", 440).attr("y", 415).text(label).style("font-size", "15px").attr("alignment-baseline", "middle");

  }

  updateChart() {
    // update scales & axis
    this.xScale.domain(this.timeRange.map((d, idx) => d + ''));
    const yLeftDomain = this.calculateYLeftDomain();
    const yRightDomain = this.calculateYRightDomain();
    this.yLeftScale.domain(yLeftDomain);
    this.yRightScale.domain(yRightDomain);

    this.xAxis.transition().call(this.axisBottom);
    this.yAxisLeft.transition().call(this.axisLeft);
    this.yAxisRight.transition().call(this.axisRight);

    const update = this.barChart.selectAll('.bar').data(this.bar.values);
    // remove exiting bars
    update.exit().remove();

    // update existing bars
    this.barChart.selectAll('.bar').transition()
      .attr('x', (d, i) => this.xScale(this.timeRange[i]))
      .attr('y', d => this.yLeftScale(d))
      .attr('width', this.xScale.bandwidth() - 2)
      .attr('height', d => this.height - this.yLeftScale(d))
      .style('fill', (d, i) => this.bar.color);

    // add new bars
    update
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', (d, i) => this.xScale(this.timeRange[i]))
      .attr('y', d => this.yLeftScale(d))
      .attr('width', this.xScale.bandwidth() - 2)
      .attr('height', d => this.height - this.yLeftScale(d))
      .style('fill', (d, i) => this.bar.color)
      .transition()
      .delay((d, i) => i * 10)
      .attr('y', d => this.yLeftScale(d))
      .attr('height', d => this.height - this.yLeftScale(d));

    // update line charts
    const lineCreate = d3.line()
      .x((d, i) => {
        return this.xScale(this.timeRange[i]) + this.xScale.bandwidth() / 2.0;
      })
      .y((d, i) => {
        return this.yRightScale(d);
      });

    const lines = this.lineCharts.selectAll('.line').data(this.lines);
    lines.exit().remove();

    this.lineCharts.selectAll('.line').transition()
      .attr('d', (d, idx) => {
        return lineCreate(d.values);
      });

    lines.enter().append('path')
      .attr('class', (d, idx) => 'line')
      .attr('fill', 'none')
      .attr('stroke', d => d.color)
      .attr('stroke-width', 1.5)
      .attr('d', (d, idx) => {
        return lineCreate(d.values);
      });

  }

  private calculateYLeftDomain(): Array<number> {
    let maxValue = d3.max(this.bar.values as number[]);
    maxValue = maxValue + (10 - (maxValue % 10));
    const scale = this.calculateTicksForYAxis(maxValue);
    return [0, scale];
  }

  private calculateYRightDomain(): Array<number> {
    let maxValue = d3.max(this.lines.map(l => d3.max(l.values as number[])));
    maxValue = maxValue + (10 - (maxValue % 10));
    const scale = this.calculateTicksForYAxis(maxValue);
    return [0, scale];
  }

  private calculateTicksForYAxis(maxValue): any {
    // we need the length of the max value
    let step = Math.pow(10, maxValue.toString().length - 1);
    // adjust stepsize based on diff
    if (maxValue / step < 2) {
      step = step / 5;
    } else if (maxValue / step < 5) {
      step = step / 2;
    }
    // add one step more to have a better visibility for the user
    const slicesCount = Math.ceil((maxValue + 1) / step);

    // if we need the tick count, return this too!
    //count: Math.min(10, slicesCount) //show max 10 ticks

    return slicesCount * step;
  }
}
